import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const sessionId = sessionStorage.getItem('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/courses');
      return;
    }
    fetchCartAndCourses();
  }, []);

  const fetchCartAndCourses = async () => {
    try {
      const [cartRes, coursesRes] = await Promise.all([
        axios.get(`${API}/cart/${sessionId}`),
        axios.get(`${API}/courses`)
      ]);
      setCart(cartRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load cart');
    }
  };

  const removeFromCart = async (courseId) => {
    try {
      await axios.delete(`${API}/cart/${sessionId}/${courseId}`);
      toast.success('Removed from cart');
      fetchCartAndCourses();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const getCartCourses = () => {
    return cart.map(item => courses.find(c => c.id === item.course_id)).filter(Boolean);
  };

  const getTotalAmount = () => {
    return getCartCourses().reduce((sum, course) => sum + course.price, 0);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Payment gateway failed to load');
        setLoading(false);
        return;
      }

      // Create Razorpay order
      const orderResponse = await axios.post(`${API}/razorpay/create-order`, {
        amount: getTotalAmount(),
        course_ids: cart.map(item => item.course_id)
      });

      const { id: order_id, amount, key_id } = orderResponse.data;

      // Razorpay options
      const options = {
        key: key_id,
        amount: amount * 100,
        currency: 'INR',
        name: 'Tinkred',
        description: 'Course Purchase',
        order_id: order_id,
        handler: async (response) => {
          try {
            // Verify payment
            const verifyResponse = await axios.post(`${API}/razorpay/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              session_id: sessionId,
              customer_email: email
            });
            toast.success('Payment successful!');
            navigate(`/thank-you/${verifyResponse.data.order_id}`);
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: '',
          email: email,
          contact: ''
        },
        theme: {
          color: '#14b8a6'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      if (error.response?.data?.detail?.includes('not configured')) {
        toast.error('Payment is not configured yet. Please contact administrator.');
      } else {
        toast.error('Payment initiation failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const cartCourses = getCartCourses();

  return (
    <div className="checkout-page min-h-screen bg-gradient-to-br from-[#f5f3f8] to-[#f5f3f8] py-20">
      <div className="container mx-auto px-6">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-800 text-center mb-12" data-testid="checkout-header">Checkout</h1>
        
        <div className="max-w-4xl mx-auto">
          {cartCourses.length === 0 ? (
            <div className="bg-white rounded-3xl shadow-xl p-12 text-center" data-testid="empty-cart">
              <p className="text-2xl text-gray-600 mb-6">Your cart is empty</p>
              <Button 
                onClick={() => navigate('/courses')}
                className="bg-[#a855f7] hover:bg-[#9333ea] text-white px-8 py-4 rounded-full"
              >
                Browse Courses
              </Button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
                {cartCourses.map((course, index) => (
                  <div key={course.id} className="bg-white rounded-2xl shadow-lg p-6 flex gap-6" data-testid={`cart-item-${index}`}>
                    <img 
                      src={course.image_url} 
                      alt={course.title} 
                      className="w-32 h-32 object-cover rounded-xl"
                    />
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">{course.title}</h3>
                      <p className="text-gray-600 mb-3">{course.description.substring(0, 80)}...</p>
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold text-[#a855f7]">₹{course.price}</span>
                        <Button 
                          data-testid={`remove-item-btn-${index}`}
                          onClick={() => removeFromCart(course.id)}
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-50 rounded-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl shadow-xl p-8 sticky top-24" data-testid="order-summary">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Order Summary</h2>
                  
                  {/* Email Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <Input
                      data-testid="customer-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-teal-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Course materials will be sent to this email</p>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal</span>
                      <span>₹{getTotalAmount()}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Tax</span>
                      <span>₹0</span>
                    </div>
                    <div className="border-t pt-4 flex justify-between text-xl font-bold text-gray-800">
                      <span>Total</span>
                      <span data-testid="total-amount">₹{getTotalAmount()}</span>
                    </div>
                  </div>
                  
                  {/* Email Input */}
                  <div className="mb-6">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14b8a6] focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <Button 
                    data-testid="proceed-payment-btn"
                    onClick={handlePayment}
                    disabled={loading}
                    className="w-full bg-[#14b8a6] hover:bg-[#0f766e] text-white py-6 text-lg rounded-full"
                  >
                    {loading ? 'Processing...' : 'Proceed to Payment'}
                    <CreditCard className="ml-2" />
                  </Button>
                  <p className="text-sm text-gray-500 text-center mt-4">Secure payment powered by Razorpay</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;